import { type Leg, type Money } from '@opentripplanner/types';
import coreUtils from '@opentripplanner/core-utils';
import {
  AERIAL_TRAM_ROUTE_ID_LABEL,
  FARE_MEDIUM_IDS,
  RIDER_CATEGORY_INFO,
  type RiderCategoryConflationEntry,
} from '#/shared/constants';

interface LegCostInfo {
  price: Money | undefined;
  isDependent: boolean;
}

/**
 * Detects if a leg fare represents an upgrade or dependent pricing scenario
 * where the amount is non-zero but reduced due to dependency on a prior leg's fare
 * @param legCostInfo The leg cost information to check
 * @returns true if this is a dependent/upgrade scenario with a non-zero reduced amount
 */
export const isUpgradeOrDependentTransfer = (
  legCostInfo: LegCostInfo | null | undefined
): boolean => {
  if (!legCostInfo?.price) {
    return false;
  }
  return legCostInfo.isDependent && legCostInfo.price.amount > 0;
};

interface ItineraryCostBreakdown {
  legCosts: Record<string, LegCostInfo>;
  totalCost: Money;
}

interface FareCalculationIds {
  mediumId: string | null;
  riderCategoryId: string | null;
}

type AllCategoriesCostBreakdown = Record<string, ItineraryCostBreakdown>;

/**
 * Gets the appropriate medium ID and rider category for fare calculation
 * The Aerial Tram has special fare handling compared to other routes
 * @return Object with mediumId and riderCategoryId to use for fare calculation
 */
export const getFareCalculationIds = (
  routeId: string | number | null,
  mediumId: string | null,
  riderCategoryId: string | null
): FareCalculationIds => {
  if (routeId != null && routeId === AERIAL_TRAM_ROUTE_ID_LABEL) {
    // Aerial Tram uses cash payment (medium ID '0') regardless of user selection
    return { mediumId: '0', riderCategoryId: null };
  }
  return { mediumId, riderCategoryId };
};

/**
 * Returns the rider category ids actually present on a leg's fare products.
 * Ids are descoped (agency prefix stripped) so they line up with the
 * unscoped ids in `RIDER_CATEGORY_CONFLATION` and what `getLegCost` expects.
 * Used by `getDetailedItineraryCost` to fall back to a leg-specific category
 * when none of the requested conflation ids are available on the leg.
 */
const getLegRiderCategoryIds = (leg: Leg): string[] =>
  Array.from(
    new Set(
      (leg.fareProducts ?? [])
        .map(fp => fp.product?.riderCategory?.id)
        .filter((id): id is string => Boolean(id))
        .map(rawId => coreUtils.itinerary.descope(rawId) ?? rawId)
    )
  );

/**
 * Builds the ordered list of rider category ids to try for a single leg
 * given a conflation group. If none of the conflation ids appear on the leg,
 * we fall back to the leg's "default" rider category. OTP does not currently
 * expose `rider_categories.default_fare_category`, so we approximate it by
 * preferring `ADULT` (the universal default in our region) and otherwise the
 * first rider category found on the leg's fare products. This prevents a leg
 * from displaying as free when the requested rider category does not exist
 * for that leg.
 */
const getLegRiderCategoryFallbackChain = (
  leg: Leg,
  preferredCategoryIds: readonly string[]
): string[] => {
  const legCategoryIds = getLegRiderCategoryIds(leg);
  const legCategorySet = new Set(legCategoryIds);

  const matched = preferredCategoryIds.filter(id => legCategorySet.has(id));
  if (matched.length > 0) return matched;

  const adultId = RIDER_CATEGORY_INFO.ADULT.id;
  if (legCategorySet.has(adultId)) return [adultId];

  return legCategoryIds.length > 0 ? [legCategoryIds[0]] : [];
};

/**
 * Resolves the cost for a single leg by trying each candidate rider category
 * in priority order, and within each category trying the preferred medium id
 * first followed by the fallback media in `FARE_MEDIUM_IDS`. Returns the first
 * non-null `getLegCost` result.
 */
const findLegCost = (
  leg: Leg,
  mediumId: string | null,
  candidateCategoryIds: readonly (string | null)[],
  seenFareIds: string[]
): ReturnType<typeof coreUtils.itinerary.getLegCost> | null => {
  const mediumIdsToTry = mediumId
    ? [mediumId, ...FARE_MEDIUM_IDS]
    : FARE_MEDIUM_IDS;

  // Try every (rider category, medium) pair and keep the first hit.
  // Reduce short-circuits once a valid fare is found.
  return candidateCategoryIds.reduce<ReturnType<
    typeof coreUtils.itinerary.getLegCost
  > | null>((found, categoryId) => {
    if (found?.appliedFareProduct?.legPrice) return found;
    return mediumIdsToTry.reduce<ReturnType<
      typeof coreUtils.itinerary.getLegCost
    > | null>((mediumFound, tryMediumId) => {
      if (mediumFound?.appliedFareProduct?.legPrice) return mediumFound;
      const result = coreUtils.itinerary.getLegCost(
        leg,
        tryMediumId,
        categoryId,
        seenFareIds
      );
      return result?.appliedFareProduct?.legPrice ? result : null;
    }, null);
  }, null);
};

/**
 * Returns detailed itinerary cost breakdown by trip ID and total cost for a
 * single conflation group (e.g. `[HONORED_CITIZEN, DISCOUNTED]`).
 *
 * Uses getLegCost from core-utils, which handles transfer detection via
 * seenFareIds. For each leg, the rider categories in the conflation group are
 * tried in order; if none are present on the leg, a default category is used
 * instead so the leg does not display as free.
 *
 * @param legs Itinerary legs with fare products (must have used getLegsWithFares)
 * @param mediumId Desired medium ID to calculate fare for
 * @param riderCategoryIds Conflation group of rider category ids to try, in priority order
 * @returns Object with leg costs by trip ID and total cost, or undefined if no costs found
 */
export function getDetailedItineraryCost(
  legs: Leg[],
  mediumId: string | null,
  riderCategoryIds: readonly string[]
): ItineraryCostBreakdown | undefined {
  const legCostsByTripId: Record<string, LegCostInfo> = {};
  const seenFareIds: string[] = [];
  let totalAmount = 0;
  let currency: Money['currency'] = { code: '', digits: 2 };

  legs
    .filter(leg => leg.fareProducts?.length && leg.trip?.id)
    .forEach(leg => {
      const routeId = typeof leg?.route === 'object' ? leg.route?.id : null;
      const { mediumId: calcMediumId } = getFareCalculationIds(
        routeId,
        mediumId,
        riderCategoryIds[0] ?? null
      );

      // The Aerial Tram uses a single fixed null category; all other legs try
      // every rider category in the conflation group, falling back to a
      // leg-specific default if none of them are present on the leg.
      const candidateCategoryIds: (string | null)[] =
        routeId === AERIAL_TRAM_ROUTE_ID_LABEL
          ? [null]
          : getLegRiderCategoryFallbackChain(leg, riderCategoryIds);

      const cost = findLegCost(
        leg,
        calcMediumId,
        candidateCategoryIds,
        seenFareIds
      );

      if (!cost?.appliedFareProduct?.legPrice || !leg.trip?.id) return;

      const tripId = leg.trip.id;
      const { appliedFareProduct, productUseId, isDependent } = cost;
      const { legPrice } = appliedFareProduct;

      // Track this fare product ID for future legs to detect transfers
      // getLegCost uses seenFareIds to return zero price for transfer legs
      if (productUseId) {
        seenFareIds.push(productUseId);
      }

      // Store the leg cost info - legPrice already accounts for transfers
      legCostsByTripId[tripId] = {
        price: legPrice,
        isDependent: isDependent || false,
      };

      // Accumulate total from the already-adjusted legPrice
      totalAmount += Number(legPrice.amount);
      currency = legPrice.currency;
    });

  if (Object.keys(legCostsByTripId).length === 0) return undefined;

  return {
    legCosts: legCostsByTripId,
    totalCost: { amount: totalAmount, currency },
  };
}

/**
 * Returns detailed itinerary cost breakdown for every conflation entry,
 * keyed by the conflation entry's display id (e.g. `ADULT`,
 * `HONORED_CITIZEN`, `YOUTH`).
 *
 * This is what populates the fare selector dropdown: one row per display
 * rider category, each row's total derived by trying the entry's mapped
 * rider category ids per leg (with the leg-default fallback in
 * `getDetailedItineraryCost`).
 *
 * @param legs Itinerary legs with fare products (must have used getLegsWithFares)
 * @param mediumId Desired medium ID to calculate fare for
 * @param conflationEntries Conflation table entries to compute breakdowns for
 * @returns Object with breakdown per conflation id, or undefined if none produced costs
 */
export function getDetailedItineraryCostForAllCategories(
  legs: Leg[],
  mediumId: string | null,
  conflationEntries: readonly RiderCategoryConflationEntry[]
): AllCategoriesCostBreakdown | undefined {
  if (conflationEntries.length === 0) return undefined;

  const results = conflationEntries.reduce<AllCategoriesCostBreakdown>(
    (acc, entry) => {
      const costData = getDetailedItineraryCost(
        legs,
        mediumId,
        entry.riderCategoryIds
      );
      if (costData) {
        return { ...acc, [entry.id]: costData };
      }
      return acc;
    },
    {}
  );

  return Object.keys(results).length > 0 ? results : undefined;
}
